import Link from 'next/link';
import React from 'react';
import { useIntl } from 'react-intl';
import { CategoryEntity } from '../../../interfaces/explore/CategoryEntity';
import { MerchantDetail } from '../../../interfaces/merchants';
import { MerchantReview } from '../../../interfaces/merchants/reviews';

import Icon from '../../../atoms/icons/Icon';
import Lightbox from '../../../components/Lightbox';
import ReviewList from '../../../molecules/merchant/reviews/ReviewList';
import Heading from '../../../atoms/Heading';
import BaseTemplate from '../../../organisms/merchants/BaseTemplate';
import DescriptionPanel from '../../../molecules/merchant/DescriptionPanel';
import MerchantProfile from '../../../molecules/merchant/MerchantProfile';
import css from './MerchantShowPage.module.scss';
import { useLineLiffAccessToken } from '../../../hooks/line-liff';
import LineMiniAppButtonLink from '../../../molecules/line/LineMiniAppButtonLink';
import { RSButtonAnchor } from 'src/reservation-page/RSButtonAnchor';
import { buildSiteUrl } from 'src/utils/request-url';

const MAX_REVIEWS = 3;

export interface PageProps {
  reviews: MerchantReview[];
  merchantDetail: MerchantDetail;
  category: CategoryEntity;
  isCoubicApp: boolean;
}

const MerchantShowPage = ({
  merchantDetail,
  reviews,
  isCoubicApp,
}: PageProps) => {
  const canShowReviews =
    merchantDetail.merchant_setting.is_customer_review_active &&
    reviews &&
    reviews.length > 0;
  const contactPageHref =
    merchantDetail.merchant_setting.contact_url || buildSiteUrl(`/contact`);
  const { lineAccessToken } = useLineLiffAccessToken(
    merchantDetail.line_liff_id,
  );
  const isNotFromLineMiniApp = lineAccessToken === null;
  const intl = useIntl();

  return (
    <div className={css.MerchantShowPage}>
      <BaseTemplate
        merchantDetail={merchantDetail}
        isCoubicApp={isCoubicApp}
        showCoverImage={true}
      >
        <div className={css.MerchantShowPage__Content}>
          {merchantDetail.line_liff_id && isNotFromLineMiniApp && (
            <div className={css.line_mini_app_link}>
              <LineMiniAppButtonLink liffId={merchantDetail.line_liff_id} />
              <p className={css.line_mini_app_description}>
                {intl.formatMessage({
                  id: 'merchants.details.canReceiveMessageFromLINE',
                })}
              </p>
            </div>
          )}
          {merchantDetail.description && (
            <div className={css.MerchantShowPage__Section}>
              <DescriptionPanel
                description_text={merchantDetail.description}
                is_json_description={false}
              />
            </div>
          )}
          {merchantDetail.images.length > 0 && (
            <div className={css.MerchantShowPage__Section}>
              <Heading className={css.MerchantShowPage__Heading}>
                {intl.formatMessage({ id: 'merchants.details.photoGallery' })}
              </Heading>
              <Lightbox
                images={merchantDetail.images.map((image) => ({
                  thumbUrl: image.thumbnail_url,
                  mainUrl: image.main_url,
                }))}
              />
            </div>
          )}

          <div className={css.MerchantShowPage__Section}>
            <Heading className={css.MerchantShowPage__Heading}>
              {intl.formatMessage(
                { id: 'merchants.details.storeData' },
                { displayName: merchantDetail.display_name },
              )}
            </Heading>
            <MerchantProfile merchantDetail={merchantDetail} />
            <div className={css.MerchantShowPage__LinkWrap}>
              <div className="mx-auto max-w-[370px]">
                <RSButtonAnchor
                  variant="outline"
                  isBlock
                  href={contactPageHref}
                >
                  {intl.formatMessage({
                    id: 'merchants.details.contactUs',
                  })}
                </RSButtonAnchor>
              </div>
            </div>
          </div>

          {canShowReviews && (
            <div className={css.MerchantShowPage__Section}>
              <Heading className={css.MerchantShowPage__Heading}>
                {intl.formatMessage({ id: 'reviews.title' })} (
                {merchantDetail.rate_count})
              </Heading>
              <ReviewList
                merchantDetail={merchantDetail}
                reviews={reviews}
                max={MAX_REVIEWS}
                rate_count_starby={merchantDetail.rate_count_starby}
              />

              {reviews.length > 0 && (
                <Link
                  href={buildSiteUrl(`/reviews#pageContent`, 'next')}
                  prefetch={false}
                  className={css.MerchantShowPage__ReviewLink}
                >
                  {intl.formatMessage({
                    id: 'merchants.details.showMoreReviews',
                  })}
                  <Icon
                    className={css.MerchantShowPage__ReviewLinkIcon}
                    name="arrow-right"
                  />
                </Link>
              )}
            </div>
          )}
          <div className={css.MerchantShowPage__BackToTopButtonSection}>
            <div className="mx-auto max-w-[370px]">
              <RSButtonAnchor variant="outline" isBlock href={buildSiteUrl()}>
                {intl.formatMessage({
                  id: 'merchants.details.backToMerchantTop',
                })}
              </RSButtonAnchor>
            </div>
          </div>
        </div>
      </BaseTemplate>
    </div>
  );
};

export default MerchantShowPage;
